// Pure flow graph for the sign-up chatbot — the branching matrix lifted out of
// the monolith's `STEP_PATHS` / `pathKey` / `currentPath` / `nextStep` computeds
// (OnboardingChatbotV1.vue). Kept side-effect-free and DOM-free so the 3-universe
// × role × MAM-injection branching is exhaustively unit-testable. The orchestrator
// (useSignupFlow) decides *which step is next*; the chat timeline decides *how* a
// step is animated. See projects-non-dev/sign-up/INSTALL.md §2 for the canonical flow.

export type Universe = 'marketplace' | 'management' | 'notebook'
export type Role = 'family' | 'childminder' | 'employer' | 'employee' | 'other'
export type SubProfile = 'mam' | 'rpe' | 'trainer' | 'agency'
export type StepKey =
  | 'universe'
  | 'mp-role'
  | 'mp-zone'
  | 'mp-caretype'
  | 'mn-role'
  | 'mn-subprofile'
  | 'mam-zone'
  | 'mn-longform'
  | 'signup'

export interface AnswerSnapshot {
  universe?: Universe | null
  role?: Role | null
  subProfile?: SubProfile | null
}

// Ordered step keys per universe+role combination. Marketplace asks the role
// BEFORE the address (mp-role → mp-zone). `other` bypasses zone/care-type and
// routes to the sub-profile + long form.
export const STEP_PATHS: Record<string, StepKey[]> = {
  marketplace_family: ['universe', 'mp-role', 'mp-zone', 'mp-caretype', 'signup'],
  marketplace_childminder: ['universe', 'mp-role', 'mp-zone', 'signup'],
  marketplace_other: ['universe', 'mp-role', 'mn-subprofile', 'mn-longform'],
  management_employer: ['universe', 'mn-role', 'signup'],
  management_employee: ['universe', 'mn-role', 'signup'],
  management_other: ['universe', 'mn-role', 'mn-subprofile', 'mn-longform'],
  notebook_employer: ['universe', 'mn-role', 'signup'],
  notebook_employee: ['universe', 'mn-role', 'signup'],
  notebook_other: ['universe', 'mn-role', 'mn-subprofile', 'mn-longform'],
}

// The longest path per universe — used as the default before a role is chosen so
// the progress bar shows a stable total from step 1 instead of jumping later.
const DEFAULT_PATH_BY_UNIVERSE: Record<string, string> = {
  marketplace: 'marketplace_family',
  management: 'management_employer',
  notebook: 'notebook_employer',
}

const FALLBACK_PATH: StepKey[] = STEP_PATHS.marketplace_family!

// `marketplace_family`, `management_other`, … or null until both universe+role known.
export function pathKey({ universe, role }: AnswerSnapshot = {}): string | null {
  if (!universe || !role) return null
  if (universe === 'marketplace') return `marketplace_${role}`
  return `${universe}_${role}`
}

// Resolves the ordered list of step keys for the current answers. Always returns a
// fresh array (never mutates STEP_PATHS). Injects `mam-zone` before `mn-longform`
// for the MAM sub-profile (the other sub-profiles collect their address in-form).
export function resolvePath({ universe, role, subProfile }: AnswerSnapshot = {}): StepKey[] {
  const key = pathKey({ universe, role })

  let source: StepKey[]
  if (key) {
    source = STEP_PATHS[key] || FALLBACK_PATH
  } else {
    const defaultKey = (universe && DEFAULT_PATH_BY_UNIVERSE[universe]) || 'marketplace_family'
    source = STEP_PATHS[defaultKey] || FALLBACK_PATH
  }
  const path = [...source]

  if (subProfile === 'mam') {
    const i = path.indexOf('mn-longform')
    if (i >= 0) path.splice(i, 0, 'mam-zone')
  }
  return path
}

// ── Universe vocabulary bridge (frontend → backend) ────────────
// The chatbot stores the frontend universe vocab (marketplace/management/notebook). The
// Google OAuth callback, however, reads omniauth.params['universe'] RAW, so the OAuth URL
// must carry the BACKEND format (child_*). This mirrors SignupChatbotForm::UNIVERSE_ALIASES
// (app/forms/signup_chatbot_form.rb) — keep the two in sync.
export type BackendUniverse = 'child_marketplace' | 'child_management' | 'child_notebook'

export const BACKEND_UNIVERSE_BY_KEY = {
  marketplace: 'child_marketplace',
  management: 'child_management',
  notebook: 'child_notebook',
} as const satisfies Record<Universe, BackendUniverse>

// Translate a frontend universe to the backend alias the OAuth callback expects. Falls
// back to child_marketplace for a null/unknown value (defensive — the funnel always sets a
// known universe before the signup step, but the OAuth URL must never be built undefined).
export function translateUniverse(universe: string | null | undefined): BackendUniverse {
  return (universe && BACKEND_UNIVERSE_BY_KEY[universe as Universe]) || 'child_marketplace'
}

// The step following `stepKey` in `path`, or null at the end / when absent.
export function nextStep(stepKey: string, path: StepKey[]): StepKey | null {
  const idx = path.indexOf(stepKey as StepKey)
  if (idx >= 0 && idx < path.length - 1) return path[idx + 1] ?? null
  return null
}
